import React, { useMemo } from "react";
import { isTotalRow, isNetRow, formatTableCellValue } from "../utils";
const SummaryReportTable = ({ title, data }) => {
  const { openingBalanceRow, cashInFlowRows, cashOutFlowRows } = useMemo(() => {
    if (!data || Array.isArray(data)) {
      return {
        openingBalanceRow: null,
        cashInFlowRows: Array.isArray(data) ? data : [],
        cashOutFlowRows: [],
      };
    }

    const allInFlow = data.CashInFlow || [];

    const openingBalanceRow = allInFlow.find(
      (row) =>
        row.Particulars?.toLowerCase().trim() === "opening balance - free cash",
    );

    const otherRows = allInFlow.filter(
      (row) =>
        row.Particulars?.toLowerCase().trim() !== "opening balance - free cash",
    );

    const inflowOrder = {
      "onshore ongoing projects": 1,
      "new customer advance": 2,
      service: 3,
      "confirming line": 4,
      "a. total collections": 5,
    };

    const sorted = [...otherRows].sort((a, b) => {
      const ao = inflowOrder[a.Particulars.toLowerCase().trim()] ?? 999;
      const bo = inflowOrder[b.Particulars.toLowerCase().trim()] ?? 999;
      return ao - bo;
    });

    const totalIndex = sorted.findIndex(
      (r) => r.Particulars.toLowerCase().trim() === "a. total collections",
    );

    const cashInFlowRows =
      totalIndex >= 0 ? sorted.slice(0, totalIndex + 1) : sorted;

    const remainingRows = totalIndex >= 0 ? sorted.slice(totalIndex + 1) : [];

    return {
      openingBalanceRow,
      cashInFlowRows,
      cashOutFlowRows: [...remainingRows, ...(data.CashOutFlow || [])],
    };
  }, [data]);

  const allRows = useMemo(() => {
    if (Array.isArray(data)) return data;

    return [
      openingBalanceRow,
      ...cashInFlowRows,
      ...(data?.CashOutFlow || []),
    ].filter(Boolean);
  }, [data, openingBalanceRow, cashInFlowRows]);

  const columns = useMemo(() => {
    if (!allRows.length) return [];

    const excludedKeys = new Set([
      "Section",
      "Particulars",
      "ParticularType",
      "Month_Year",
    ]);

    return Object.keys(allRows[0]).filter((key) => !excludedKeys.has(key));
  }, [allRows]);

  if (!allRows.length) return null;

  const renderRow = (row, index) => {
    const isTotal = isTotalRow(row.Particulars);
    const isNet = isNetRow(row.Particulars);

    if (isNet) {
      return (
        <tr
          key={`${row.Particulars}-${index}`}
          className="border-t-2 border-[#633806]"
        >
          <td className="px-4 py-2.5 font-bold text-[#FAEEDA] bg-[#854F0B]">
            {row.Particulars}
          </td>

          {columns.map((col) => (
            <td
              key={col}
              className="px-4 py-2.5 text-right font-mono font-bold text-[#FAEEDA] bg-[#854F0B]"
            >
              {formatTableCellValue(row[col])}
            </td>
          ))}
        </tr>
      );
    }

    if (isTotal) {
      return (
        <tr
          key={`${row.Particulars}-${index}`}
          className="border-t-2 border-[#EF9F27]"
        >
          <td className="px-4 py-2.5 font-bold text-[#412402] bg-[#FAC775]">
            {row.Particulars}
          </td>

          {columns.map((col) => (
            <td
              key={col}
              className="px-4 py-2.5 text-right font-mono font-bold text-[#412402] bg-[#FAC775]"
            >
              {formatTableCellValue(row[col])}
            </td>
          ))}
        </tr>
      );
    }

    return (
      <tr
        key={`${row.Particulars}-${index}`}
        className="border-t border-[#FAF0DC] hover:bg-[#FEF8EE]"
      >
        <td className="px-4 py-2.5 font-medium text-[#5a3a1a]">
          {row.Particulars}
        </td>

        {columns.map((col) => {
          const value = Number(row[col]);

          return (
            <td
              key={col}
              className={`px-4 py-2.5 text-right font-mono ${
                value < 0 ? "text-red-700" : value > 0 ? "text-emerald-700" : ""
              }`}
            >
              {formatTableCellValue(row[col])}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderSubheading = (text) => (
    <tr key={text} className="bg-[#FAEEDA]">
      <th
        colSpan={columns.length + 1}
        className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[#854F0B]"
      >
        {text}
      </th>
    </tr>
  );

  return (
    <div className="rounded-2xl border border-[#EFD99A] bg-white shadow-sm">
      <div className="border-b border-[#EFD99A] px-4 py-3">
        <h2 className="text-lg font-semibold text-[#412402]">{title}</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-[#854F0B] text-left text-xs font-semibold uppercase tracking-wider text-[#FAEEDA]">
            <tr>
              <th className="w-1/3 px-4 py-3">Particulars</th>

              {columns.map((col) => (
                <th key={col} className="px-4 py-3 text-right">
                  {col.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Array.isArray(data) ? (
              data.map((row, index) => renderRow(row, index))
            ) : (
              <>
                {/* Opening Balance */}
                {openingBalanceRow && renderRow(openingBalanceRow, "opening")}

                {/* Cash Inflow */}
                {cashInFlowRows.length > 0 && (
                  <>
                    {renderSubheading("Cash Inflow")}
                    {cashInFlowRows.map((row, index) =>
                      renderRow(row, `in-${index}`),
                    )}
                  </>
                )}

                {/* Cash Outflow (API order unchanged) */}
                {cashOutFlowRows.length > 0 && (
                  <>
                    {renderSubheading("Cash Outflow")}
                    {cashOutFlowRows.map((row, i) =>
                      renderRow(row, `out-${i}`),
                    )}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SummaryReportTable;
